import {
    faCalendar,
    faCalendarDays,
    faCircleDollar,
    faFileSignature,
    faHeartPulse,
    faImagesUser,
    faPaw,
    faPhoneFlip,
    faPlus,
    faStar
} from '@fortawesome/pro-light-svg-icons';
import { faCommentAltLines, faGauge } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Typography, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { Client as ClientModel, createEmptyClient } from '@spike/client-model';
import { clearRetrievedClientThunk, ClientsStatus, getClientThunk } from '@spike/clients-action';
import { createEmptyPet, Pet as PetModel } from '@spike/pet-model';
import { fetchPetsThunk, PetsStatus } from '@spike/pets-action';
import useNonInitialEffect from '@versiondos/hooks';
import { fetchOnboardingStepsThunk, onboardingCloseModal } from 'actions/onboardingSteps/OnboardingStepsActions';
import ClientSignatures from 'components/Agreements/ClientSignatures';
import ClientComponent from 'components/Client';
import PageSidebar from 'components/PageSidebar';
import { PageSidebarItem } from 'components/PageSidebar/PageSidebarModel';
import AddPet from 'components/Pet/AddPet';
import EditPet, { appointmentsHistoryOption } from 'components/Pet/EditPet';
import { Button, Spinner } from 'components/UI';
import ButtonV3 from 'components/UI/V3/Button/Button';
import {
    useApiClientWrapper,
    useAuth,
    useHasPremiumAccess,
    useMarketplace,
    useMasterData,
    usePermissions
} from 'hooks';
import { isPaymentsEnabled } from 'utils/MarketingUtils';
import { EnrollmentStatus } from '@spike/payments-model';
import { getEnrollmentStatusThunk, PaymentsStatus } from '@spike/payments-action';
import { isNewAccount } from 'utils/PaymentsUtils';
import isEmpty from 'lodash/isEmpty';
import PrivatePage from 'pages/PrivatePage';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { reduceResolution, wbp } from 'Theme';
import { formatPhoneNumber } from '@spike/phone-utils';
import { v4 as uuid } from 'uuid';
import { UnderlinedButton } from 'components/UI/UnderlinedButton';
import { ActionsPopup } from 'components/UI/ActionsPopup/ActionsPopup';
import { ClientEditHeader } from 'components/Client/ClientEditHeader';
import CreateBookingDrawer from 'components/UI/CreateBookingDrawer';
import PremiumFeatureAlert from 'components/UI/PremiumFeatureAlert';
import Tag from 'components/UI/V2/Tag/Tag';
import ClientCards from 'components/ClientCards/ClientCards';
import { faFileAlt, faFolder, faMedal, faMemoCircleCheck, faWallet } from '@fortawesome/pro-regular-svg-icons';
import { ClientIntakeForm } from 'components/ClientIntakeForm/ClientIntakeForm';
import ClientForms from 'components/ClientForms/ClientForms';
import { AppointmentsList } from 'components/Bookings2/List/AppointmentsList';
import { ClientDocuments } from 'components/ClientDocuments/ClientDocuments';
import ClientTransactions from 'components/ClientTransactions/ClientTransactions';
import TwoWaySMSDrawer from 'components/UI/TwoWaySMSDrawer/TwoWaySMSDrawer';
import { fetchTwoWaySmsCountByCustomerThunk } from 'actions/twoWaySms/twoWaySmsActions';
import ClientMemberships from 'components/ClientMemberships';
import ClientWallet from 'components/ClientWallet/ClientWallet';
import ClientRewards from 'components/ClientRewards';
import ClientOverview from 'components/Client/ClientOverview';

interface ClientProps {
    clientId?: number;
    isNewBooking?: boolean;
    petId?: number;
    hideHeader?: boolean;
    onCloseModal?: () => void;
    onClose?: () => void;
    onClientSaved?: (client: ClientModel) => void;
    onPetSaved?: (pet: PetModel) => void;
    onDelete?: () => void;
}

interface BookingNew {
    clientId: number;
    petId?: number;
    clientName: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            width: '100%',
            height: '100%',
            display: 'flex',
            flexFlow: 'column'
        },
        page: {
            backgroundColor: '#ffffff',

            [theme.breakpoints.up('sm')]: {
                backgroundColor: '#FAFAFA'
            }
        },
        spinner: {
            [theme.breakpoints.down(wbp)]: {
                marginTop: `${reduceResolution(100)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                marginTop: '100px'
            }
        },
        clientInfo: {
            gap: 6,
            display: 'flex',
            flexDirection: 'column'
        },
        name: {
            fontFamily: 'Poppins',
            fontWeight: 600,
            color: '#222222',
            margin: 0,
            lineHeight: 1.2,
            [theme.breakpoints.down(wbp)]: {
                fontSize: `${reduceResolution(16)}px`,
                paddingLeft: `${reduceResolution(5)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                fontSize: '16px',
                paddingLeft: '5px'
            }
        },
        nameRowText: {
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center'
        },
        phoneContainer: {
            display: 'flex',
            alignItems: 'center',
            [theme.breakpoints.down(wbp)]: {
                paddingLeft: `${reduceResolution(5)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                paddingLeft: '5px'
            }
        },
        phone: {
            fontFamily: 'Poppins',
            fontWeight: 400,
            color: '#222222',
            [theme.breakpoints.down(wbp)]: {
                fontSize: `${reduceResolution(15)}px`,
                marginLeft: `${reduceResolution(7)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                fontSize: '15px',
                marginLeft: '7px'
            }
        },
        phoneIcon: {
            [theme.breakpoints.down(wbp)]: {
                fontSize: `${reduceResolution(16)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                fontSize: '16px'
            }
        },
        headerRow: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            width: '100%'
        },
        chatButtonContainer: {
            position: 'relative',
            flexShrink: 0
        },
        chatButton: {
            'borderColor': '#000',
            'borderRadius': 30,
            'height': 39,
            'minWidth': 'unset',
            'width': 39,
            'padding': 0,

            '& span': {
                paddingTop: '1px',
                lineHeight: 1,
                margin: '0px !important'
            },

            '& svg': {
                width: 18,
                height: 'auto'
            }
        },
        notificationBadge: {
            top: -5,
            left: -3,
            color: '#fff',
            fontSize: 13,
            lineHeight: '12.143px',
            display: 'flex',
            fontWeight: 600,
            borderRadius: 3,
            transform: 'none',
            alignItems: 'center',
            padding: '2px 5px 1px',
            justifyContent: 'center',
            backgroundColor: '#EF4F57',
            width: 20,
            height: 20,
            position: 'absolute'
        },
        mobileTitle: {
            fontSize: 18,
            lineHeight: 1,
            fontWeight: 600,
            marginBottom: 24,

            [theme.breakpoints.up('md')]: {
                display: 'none'
            }
        },
        newSaleButton: {
            width: '100%'
        },
        fullWidthButton: {
            width: '100%'
        }
    })
);

export const ClientKey = 'client';
export const OverviewKey = 'overview';
export const PetsKey = 'pets';
export const PrefixPetKey = 'pet:';
export const AddPetKey = 'add_pet';
export const HistoryKey = 'history';
export const IntakeFormKey = 'intake_form';
export const AgreementsKey = 'agreements';
export const CardsKey = 'cards';
export const MembershipsKey = 'memberships';
export const FormsKey = 'forms';
export const DocumentsKey = 'documents';
export const TransactionsKey = 'transactions';
export const WalletKey = 'wallet';
export const RewardsKey = 'rewards';

const agreementsEnabled = process.env.REACT_APP_AGREEMENTS_ENABLED === 'true';

const pageBodyId = 'client-page-body-id';

export const Client: FunctionComponent<ClientProps> = props => {
    const classes = useStyles();

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const auth = useAuth();
    const marketplace = useMarketplace();
    const hasReportsPremiumAccess = useHasPremiumAccess('agreements');
    const masterData = useMasterData();

    const permissions = usePermissions();
    const twoWaySmsEnabled = permissions.twoWaySms;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
    const [showActions, setShowActions] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);

    const client = useSelector<RootState, ClientModel | undefined>(state => state.clients.client);
    const clientsStatus = useSelector<RootState, ClientsStatus>(state => state.clients.status);
    const smsCountByCustomer = useSelector<RootState, number | null>(state => state.twoWaySms.customerMessageAmount);

    const pets = useSelector<RootState, Array<PetModel>>(state => state.pets.list);
    const petsStatus = useSelector<RootState, PetsStatus>(state => state.pets.status);
    const paymentsEnrollmentStatus = useSelector<RootState, EnrollmentStatus | undefined>(
        state => state.payments.enrollmentStatus
    );
    const paymentsStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);

    const [addPet, setAddPet] = useState<PetModel | null>(null);
    const [editPet, setEditPet] = useState<PetModel | null>(null);

    const [petNotesEditedComponent, setPetNotesEditedComponent] = useState(false);

    const [initialPet, setInitialPet] = useState<PetModel | null>(null);
    const [clientLoading, setClientLoading] = useState(true);
    const [petsLoading, setPetsLoading] = useState(true);
    const [sidebarKey, setSidebarKey] = useState(props.petId ? `${PrefixPetKey}${props.petId}` : ClientKey);
    const [showNewBooking, setShowNewBooking] = useState<BookingNew | undefined>(undefined);
    const clientsDone = useSelector<RootState, boolean>(state => state.onboardingSteps.onboardingSteps.clientsDone);
    const [showNewPet, setShowNewPet] = useState<boolean>(false);
    const [petAdded, setPetAdded] = useState(false);
    const [showTwoWaySms, setShowTwoWaySms] = useState(false);

    const [history, setHistory] = useState<{ client: ClientModel | null; showingPetHistory: boolean }>({
        client: null,
        showingPetHistory: false
    });

    const clientHasIntakeFormResponses = React.useMemo(() => {
        return (
            (client && client?.customQuestionResponses.length > 0) ||
            pets.some(pet => pet.customQuestionResponses.length > 0)
        );
    }, [client, pets]);

    const onBookClientHandler = () => {
        if (!client?.id) {
            return;
        }

        dispatch(fetchPetsThunk(apiClientWrapper, client.id!));

        setShowNewBooking({
            clientId: client.id!,
            clientName: `${client.firstName} ${client.lastName}`,
            petId: editPet?.id
        });
    };

    const featureFlag = process.env.REACT_APP_SINGLE_PAYMENT_ENABLED?.toLowerCase() === 'true';
    const paymentsCountryEnabled = isPaymentsEnabled(marketplace);
    const userHasPaymentsAccess = permissions.checkout.takePayments;
    const canAccessClientWallet = permissions.checkout.takePayments;
    const isNew = isNewAccount(paymentsEnrollmentStatus);
    const cuddlesPayReady = paymentsEnrollmentStatus
        ? isNew
            ? paymentsEnrollmentStatus.verificationStatus === 'valid'
            : paymentsEnrollmentStatus.enabled
        : false;

    const canCreateSale = featureFlag && paymentsCountryEnabled && userHasPaymentsAccess && cuddlesPayReady;
    const canAccessClientTransactions = canCreateSale;

    // Checkout re-fetches enrollment status; do not reset the active section (e.g. back to Personal Information).
    const preserveSidebarOnEnrollmentRefresh = [TransactionsKey];
    const isPreservingSidebarOnEnrollmentRefresh =
        preserveSidebarOnEnrollmentRefresh.includes(sidebarKey) &&
        paymentsStatus === PaymentsStatus.GettingEnrollmentStatus;
    const effectiveCanAccessClientTransactions = canAccessClientTransactions || isPreservingSidebarOnEnrollmentRefresh;

    const onNewSaleHandler = canCreateSale
        ? () => {
              if (!client?.id) {
                  return;
              }
              sessionStorage.setItem('singleSaleFromClientId', String(client.id));
              window.location.href = `/payments#SINGLE_SALE`;
          }
        : undefined;

    const [isOnBoardingPetActive, setIsOnBoardingPetActive] = useState(false);

    useEffect(() => {
        dispatch(clearRetrievedClientThunk());
        dispatch(getEnrollmentStatusThunk(apiClientWrapper));
        if (props.clientId) {
            dispatch(getClientThunk(apiClientWrapper, props.clientId));
            dispatch(fetchPetsThunk(apiClientWrapper, props.clientId));
        } else {
            setClientLoading(false);
        }
    }, [props.clientId]);

    useEffect(() => {
        if (client?.id) {
            dispatch(fetchTwoWaySmsCountByCustomerThunk(client.id));
        }
    }, [client?.id, dispatch]);

    useEffect(() => {
        if (!effectiveCanAccessClientTransactions && sidebarKey === TransactionsKey) {
            setSidebarKey(ClientKey);
        }
    }, [effectiveCanAccessClientTransactions, sidebarKey]);

    useEffect(() => {
        if (!canAccessClientWallet && sidebarKey === WalletKey) {
            setSidebarKey(ClientKey);
        }
    }, [canAccessClientWallet, sidebarKey]);

    useNonInitialEffect(() => {
        if (ClientsStatus.GetSuccess === clientsStatus) {
            if (editPet?.id && !history.showingPetHistory) {
                setEditPet(null);
                setSidebarKey(ClientKey);
            }
            if (props.isNewBooking) {
                setAddPet(createEmptyPet(uuid, props.clientId!));
                setSidebarKey(PetsKey);
            }
            setClientLoading(false);
            if (isOnBoardingPetActive) {
                setAddPet(createEmptyPet(uuid, client?.id!));
                containerRef.current!.scrollTop = 0;
                dispatch(onboardingCloseModal());
                setIsOnBoardingPetActive(false);
            }
        } else if (ClientsStatus.SaveSuccess === clientsStatus) {
            dispatch(onboardingCloseModal());
            setClientLoading(false);
        }
    }, [clientsStatus]);

    useNonInitialEffect(() => {
        if (PetsStatus.FetchSuccess === petsStatus) {
            if (props.petId && initialPet === null) {
                const pet = pets.find(pet => pet.id === props.petId) || null;
                setEditPet(pet);
                setInitialPet(pet);
            }
            setPetsLoading(false);
        }
    }, [petsStatus]);

    const title = client?.id === undefined ? 'Add Client' : 'Edit Client';

    const info = client ? (
        <Box className={classes.clientInfo}>
            <Box className={classes.headerRow}>
                <Box className={classes.nameRowText}>
                    <Typography className={classes.name}>{`${client?.firstName} ${client?.lastName}`}</Typography>
                </Box>
                {twoWaySmsEnabled && client.id !== undefined && (
                    <div className={classes.chatButtonContainer}>
                        <Button
                            startIcon={faCommentAltLines}
                            color="black"
                            variant="secondary"
                            onClick={() => setShowTwoWaySms(true)}
                            className={classes.chatButton}
                            size="medium"
                        />
                        {(smsCountByCustomer ?? 0) > 0 && (
                            <span className={classes.notificationBadge}>{smsCountByCustomer}</span>
                        )}
                    </div>
                )}
            </Box>

            {!isEmpty(client?.phone) && (
                <Box className={classes.phoneContainer}>
                    <FontAwesomeIcon
                        icon={faPhoneFlip}
                        className={classes.phoneIcon}
                    />
                    <Typography className={classes.phone}>
                        {formatPhoneNumber(client?.phone, apiClientWrapper.marketplace!.basics.address.country!.id)}
                    </Typography>
                </Box>
            )}

            {client.blocked && (
                <Box>
                    <Tag variant="danger">Blocked</Tag>
                </Box>
            )}

            {!client.blocked && (
                <>
                    <ButtonV3
                        size="small"
                        label="Book appointment"
                        variant="green"
                        onClick={onBookClientHandler}
                        className={classes.fullWidthButton}
                    />
                    {onNewSaleHandler && (
                        <ButtonV3
                            size="small"
                            label="New Sale"
                            color="green"
                            variant="outlined"
                            onClick={onNewSaleHandler}
                            className={classes.newSaleButton}
                        />
                    )}
                </>
            )}
        </Box>
    ) : undefined;

    const sidebarItems: Array<PageSidebarItem> = [
        { icon: faGauge, title: 'Overview', key: OverviewKey },
        { icon: faImagesUser, title: 'Client Information', key: ClientKey },
        {
            icon: faPaw,
            title: 'Pets',
            key: PetsKey,
            subitems: [
                ...(pets || []).map(pet => ({
                    title: `${pet.deceased ? '🌈 ' : ''}${pet.name}`,
                    key: `${PrefixPetKey}${pet.id}`
                })),
                {
                    icon: faPlus,
                    title: 'Add Pet',
                    key: AddPetKey,
                    isLink: true
                }
            ]
        },
        ...(clientHasIntakeFormResponses
            ? [{ icon: faMemoCircleCheck, title: 'Intake Form', key: IntakeFormKey }]
            : []),
        { icon: faFileAlt, title: 'Forms', key: FormsKey },
        { icon: faFolder, title: 'Documents', key: DocumentsKey },
        ...(canAccessClientWallet ? [{ icon: faWallet, title: 'Wallet', key: WalletKey }] : []),
        { icon: faMedal, title: 'Rewards', key: RewardsKey },
        { icon: faStar, title: 'Memberships', key: MembershipsKey },
        { icon: faCalendar, title: 'History', key: HistoryKey },
        ...(effectiveCanAccessClientTransactions
            ? [{ icon: faCircleDollar, title: 'Transactions', key: TransactionsKey }]
            : []),
        { icon: faFileSignature, title: 'Agreements', key: AgreementsKey }
    ]
        .filter(item => client?.id !== undefined || item.key === ClientKey)
        .filter(item => (agreementsEnabled && hasReportsPremiumAccess) || item.key !== AgreementsKey)
        .filter(item => permissions.memberships.viewSold || item.key !== MembershipsKey);

    const selectKeyHandler = (key: string, containerRef: React.RefObject<HTMLDivElement>) => {
        if (key === TransactionsKey && !canAccessClientTransactions) {
            setSidebarKey(ClientKey);
            containerRef.current!.scrollTop = 0;
            setShowActions(false);
            return;
        }

        if (key === WalletKey && !canAccessClientWallet) {
            setSidebarKey(ClientKey);
            containerRef.current!.scrollTop = 0;
            setShowActions(false);
            return;
        }

        setSidebarKey(key);
        containerRef.current!.scrollTop = 0;
        setShowActions(false);

        switch (key) {
            case OverviewKey:
            case ClientKey:
                setEditPet(null);
                setAddPet(null);
                break;
            case AddPetKey:
                setAddPet(createEmptyPet(uuid, client?.id!));
                setEditPet(null);
                break;
            case AgreementsKey:
                setAddPet(null);
                setEditPet(null);
                break;
            case CardsKey:
            case MembershipsKey:
            case WalletKey:
            case RewardsKey:
                setAddPet(null);
                setEditPet(null);
                break;
            case FormsKey:
            case DocumentsKey:
                setAddPet(null);
                setEditPet(null);
                break;
            case HistoryKey:
            case TransactionsKey:
                setAddPet(null);
                setEditPet(null);
                if (key === HistoryKey) {
                    setHistory(prev => ({ ...prev, client: client ?? null }));
                }
                break;
            default:
                const selectedPetId = Number(key.slice(PrefixPetKey.length));
                setEditPet(pets.find(pet => pet.id === selectedPetId) || null);
                setAddPet(null);
        }
    };

    const reload = (client: ClientModel) => {
        setClientLoading(true);
        setPetsLoading(true);
        if (client) {
            dispatch(getClientThunk(apiClientWrapper, client.id!));
            dispatch(fetchPetsThunk(apiClientWrapper, client.id!));
        }
    };

    const saveClientHandler = (savedClient: ClientModel) => {
        reload(savedClient);

        if (clientsDone === false) {
            setIsOnBoardingPetActive(true);
            selectKeyHandler(PetsKey, containerRef);
        }

        props.onClientSaved && props.onClientSaved(savedClient);
    };

    const savePetHandler = (pet: PetModel, petNotesEdited: boolean) => {
        reload(client!);
        setAddPet(null);
        setEditPet({ ...pet });

        if (clientsDone === false) {
            dispatch(fetchOnboardingStepsThunk());
        }

        setPetNotesEditedComponent(petNotesEdited);

        props.onPetSaved && props.onPetSaved({ ...pet });
    };

    const saveAddPetHandler = (pet: PetModel, containerRef: React.RefObject<HTMLDivElement>) => {
        savePetHandler(pet, false);
        setSidebarKey(`${PrefixPetKey}${pet.id}`);
        containerRef.current!.scrollTop = 0;
        setShowNewPet(false);
        setPetAdded(true);
    };

    const deletePetHandler = (containerRef: React.RefObject<HTMLDivElement>) => {
        setSidebarKey(ClientKey);
        setEditPet(null);
        setAddPet(null);
        containerRef.current!.scrollTop = 0;
        reload(client!);
    };

    const backToClientInformation = () => {
        setSidebarKey(ClientKey);
        setEditPet(null);
        setAddPet(null);
    };

    const addNewPet = () => {
        setAddPet(createEmptyPet(uuid, props.clientId!));
        setShowNewPet(true);
    };

    const handleBackNewBooking = () => {
        setShowNewBooking(undefined);
        setPetAdded(false);
        setShowNewPet(false);
    };

    const sidebar = (
        <PageSidebar
            title={title}
            info={info}
            items={sidebarItems}
            selectedKey={sidebarKey}
            onSelect={key => selectKeyHandler(key, containerRef)}
            onBack={props.onClose}
        />
    );

    const newBookingView = (
        <CreateBookingDrawer
            parentID={showNewBooking?.clientId}
            parentName={showNewBooking?.clientName}
            petID={showNewBooking?.petId}
            fromClient={true}
            onBooked={handleBackNewBooking}
            onClose={handleBackNewBooking}
            petAdded={petAdded}
            showTabs={false}
            onAddPet={addNewPet}
        />
    );

    const openActionsMobile = () => {
        setShowActions(true);
    };

    const clientActionsPopup = (
        <ActionsPopup
            actions={[
                {
                    label: 'Overview',
                    icon: faGauge,
                    onClick: () => selectKeyHandler(OverviewKey, containerRef)
                },
                {
                    label: 'Client Information',
                    icon: faImagesUser,
                    onClick: () => selectKeyHandler(ClientKey, containerRef)
                },
                {
                    icon: faPaw,
                    label: 'Pets',
                    items: pets.map(pet => ({
                        label: (
                            <>
                                {pet.name}
                                {pet.deceased ? ' 🌈' : ''}
                                {pet.hasMedicalConditions && (
                                    <FontAwesomeIcon
                                        size="lg"
                                        color="#EF4F57"
                                        icon={faHeartPulse}
                                        style={{ marginLeft: 4 }}
                                    />
                                )}
                            </>
                        ),
                        onClick: () => selectKeyHandler(`${PrefixPetKey}${pet.id}`, containerRef)
                    })),
                    rightElement: (
                        <UnderlinedButton
                            color="green"
                            label="Add Pet"
                            leftIcon={<FontAwesomeIcon icon={faPlus} />}
                            onClick={() => selectKeyHandler(AddPetKey, containerRef)}
                        />
                    ),
                    onClick: () => selectKeyHandler(PetsKey, containerRef)
                },
                {
                    label: 'Documents',
                    icon: faFolder,
                    onClick: () => selectKeyHandler(DocumentsKey, containerRef)
                },
                ...(canAccessClientWallet
                    ? [
                          {
                              label: 'Wallet',
                              icon: faWallet,
                              onClick: () => selectKeyHandler(WalletKey, containerRef)
                          }
                      ]
                    : []),
                {
                    label: 'Rewards',
                    icon: faMedal,
                    onClick: () => selectKeyHandler(RewardsKey, containerRef)
                },
                {
                    label: 'Memberships',
                    icon: faStar,
                    onClick: () => selectKeyHandler(MembershipsKey, containerRef)
                },
                {
                    label: 'History',
                    icon: faCalendarDays,
                    onClick: () => selectKeyHandler(HistoryKey, containerRef)
                },
                ...(effectiveCanAccessClientTransactions
                    ? [
                          {
                              label: 'Transactions',
                              icon: faCircleDollar,
                              onClick: () => selectKeyHandler(TransactionsKey, containerRef)
                          }
                      ]
                    : []),
                {
                    label: 'Agreements',
                    icon: faFileSignature,
                    onClick: () => selectKeyHandler(AgreementsKey, containerRef)
                }
            ]}
            onClose={() => setShowActions(false)}
        />
    );

    return (
        <PrivatePage
            title="Client"
            pageName="/client"
            pageSidebar={sidebar}
            className={classes.page}
            hideHeader={props.hideHeader}
            onClose={props.onCloseModal}
            pageBodyId={pageBodyId}
        >
            <div
                ref={containerRef}
                className={classes.container}
            >
                {(clientLoading || (petsLoading && props.petId)) && <Spinner className={classes.spinner} />}
                {!clientLoading && editPet !== null && (client || history.showingPetHistory) && (
                    <EditPet
                        pet={editPet!}
                        onDeleted={() => deletePetHandler(containerRef)}
                        onSaved={savePetHandler}
                        editPetNotes={petNotesEditedComponent}
                        onSelect={backToClientInformation}
                        onSelectDropdown={key => selectKeyHandler(key, containerRef)}
                        onClikedActions={openActionsMobile}
                        onChangeViewId={viewId =>
                            setHistory(prev => ({
                                ...prev,
                                showingPetHistory: viewId === appointmentsHistoryOption.id
                            }))
                        }
                    />
                )}
                {!clientLoading && addPet !== null && (
                    <AddPet
                        pet={addPet!}
                        onDeleted={() => deletePetHandler(containerRef)}
                        onSaved={pet => saveAddPetHandler(pet, containerRef)}
                        onSelect={backToClientInformation}
                    />
                )}
                {!clientLoading && addPet === null && editPet === null && sidebarKey === ClientKey && (
                    <ClientComponent
                        client={client || createEmptyClient(uuid)}
                        onSaved={saveClientHandler}
                        onBook={onBookClientHandler}
                        onNewSale={onNewSaleHandler}
                        onClose={() => props.onClose && props.onClose()}
                        onDeleted={() => (props.onDelete ? props.onDelete() : props.onClose && props.onClose())}
                        onSelect={key => selectKeyHandler(key, containerRef)}
                    />
                )}
                {!clientLoading && client && addPet === null && editPet === null && sidebarKey === OverviewKey && (
                    <ClientOverview
                        client={client}
                        onBook={onBookClientHandler}
                        onNewSale={onNewSaleHandler}
                        onClose={() => props.onClose && props.onClose()}
                        onSelect={key => selectKeyHandler(key, containerRef)}
                        showTransactions={effectiveCanAccessClientTransactions}
                    />
                )}
                {!clientLoading && addPet === null && editPet === null && sidebarKey === AgreementsKey && client && (
                    <>
                        {hasReportsPremiumAccess ? (
                            <ClientSignatures
                                client={client!}
                                onBook={onBookClientHandler}
                                onNewSale={onNewSaleHandler}
                                onClose={() => props.onClose && props.onClose()}
                                onSelect={key => selectKeyHandler(key, containerRef)}
                            />
                        ) : (
                            <PremiumFeatureAlert
                                text={
                                    <>
                                        Keep clients informed of your policies with Agreements. Create custom
                                        agreements, send via email or text, and automate signature collection.{' '}
                                        <b>Upgrade now</b> and ditch paper agreements.
                                    </>
                                }
                                isOpen={true}
                                onCancel={() => selectKeyHandler(ClientKey, containerRef)}
                            />
                        )}
                    </>
                )}

                {!clientLoading &&
                    addPet === null &&
                    editPet === null &&
                    sidebarKey === MembershipsKey &&
                    client &&
                    client.id && (
                        <Box style={{ backgroundColor: '#FAFAFA' }}>
                            <ClientMemberships
                                client={client}
                                onBook={onBookClientHandler}
                                onClose={() => props.onClose && props.onClose()}
                                onSelect={key => selectKeyHandler(key, containerRef)}
                            />
                        </Box>
                    )}

                {!clientLoading &&
                    addPet === null &&
                    editPet === null &&
                    sidebarKey === CardsKey &&
                    client &&
                    client.id && (
                        <>
                            <ClientCards
                                clientId={client.id}
                                client={client!}
                                onBook={onBookClientHandler}
                                onNewSale={onNewSaleHandler}
                                onClose={() => props.onClose && props.onClose()}
                                onSelect={key => selectKeyHandler(key, containerRef)}
                            />
                        </>
                    )}

                {!clientLoading &&
                    history.client &&
                    addPet === null &&
                    editPet === null &&
                    sidebarKey === HistoryKey && (
                        <>
                            <Box
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: 1,
                                    backgroundColor: '#fff'
                                }}
                            >
                                <Box
                                    style={{
                                        padding: '24px 16px 0px'
                                    }}
                                >
                                    <ClientEditHeader
                                        client={history.client}
                                        onBook={onBookClientHandler}
                                        onNewSale={onNewSaleHandler}
                                        showTransactions={effectiveCanAccessClientTransactions}
                                        onClose={() => props.onClose && props.onClose()}
                                        onSelect={key => selectKeyHandler(key, containerRef)}
                                    />

                                    <Typography
                                        variant="h3"
                                        className={classes.mobileTitle}
                                    >
                                        History
                                    </Typography>
                                </Box>
                                <AppointmentsList
                                    listType="client"
                                    clientId={history.client.id!}
                                    scrollableContainerId={pageBodyId}
                                />
                            </Box>
                        </>
                    )}

                {!clientLoading && client && addPet === null && editPet === null && sidebarKey === IntakeFormKey && (
                    <ClientIntakeForm
                        pets={pets}
                        client={client}
                        onBook={onBookClientHandler}
                        onNewSale={onNewSaleHandler}
                        onSelect={key => selectKeyHandler(key, containerRef)}
                        onClose={() => props.onClose && props.onClose()}
                    />
                )}

                {!clientLoading && client && addPet === null && editPet === null && sidebarKey === FormsKey && (
                    <Box style={{ backgroundColor: '#fff' }}>
                        <ClientForms
                            clientId={client.id!}
                            client={client!}
                            onBook={onBookClientHandler}
                            onNewSale={onNewSaleHandler}
                            onClose={() => props.onClose && props.onClose()}
                            onSelect={key => selectKeyHandler(key, containerRef)}
                        />
                    </Box>
                )}

                {!clientLoading &&
                    client &&
                    client.id &&
                    addPet === null &&
                    editPet === null &&
                    effectiveCanAccessClientTransactions &&
                    sidebarKey === TransactionsKey && (
                        <ClientTransactions
                            client={client}
                            onBook={onBookClientHandler}
                            onClose={() => props.onClose && props.onClose()}
                            onSelect={key => selectKeyHandler(key, containerRef)}
                            showTransactions={effectiveCanAccessClientTransactions}
                        />
                    )}

                {!clientLoading &&
                    client &&
                    client.id &&
                    addPet === null &&
                    editPet === null &&
                    sidebarKey === DocumentsKey && (
                        <Box style={{ backgroundColor: '#fff' }}>
                            <ClientDocuments
                                clientRef={client}
                                clientId={client.id}
                                onBook={onBookClientHandler}
                                onNewSale={onNewSaleHandler}
                                onClose={() => props.onClose && props.onClose()}
                                onSelect={key => selectKeyHandler(key, containerRef)}
                            />
                        </Box>
                    )}

                {!clientLoading &&
                    client &&
                    client.id &&
                    addPet === null &&
                    editPet === null &&
                    canAccessClientWallet &&
                    sidebarKey === WalletKey && (
                        <Box style={{ backgroundColor: '#FAFAFA' }}>
                            <ClientWallet
                                clientRef={client}
                                clientId={client.id}
                                onBook={onBookClientHandler}
                                onNewSale={onNewSaleHandler}
                                onClose={() => props.onClose && props.onClose()}
                                onSelect={key => selectKeyHandler(key, containerRef)}
                            />
                        </Box>
                    )}

                {!clientLoading &&
                    client &&
                    client.id &&
                    addPet === null &&
                    editPet === null &&
                    sidebarKey === RewardsKey && (
                        <Box style={{ backgroundColor: '#FAFAFA' }}>
                            <ClientRewards
                                clientRef={client}
                                clientId={client.id}
                                onBook={onBookClientHandler}
                                onNewSale={onNewSaleHandler}
                                onClose={() => props.onClose && props.onClose()}
                                onSelect={key => selectKeyHandler(key, containerRef)}
                            />
                        </Box>
                    )}

                {showNewBooking && !showNewPet && newBookingView}
            </div>

            {showActions && clientActionsPopup}

            {client?.id && (
                <TwoWaySMSDrawer
                    key={client.id}
                    hide={!showTwoWaySms}
                    onBack={() => setShowTwoWaySms(false)}
                    onClose={() => setShowTwoWaySms(false)}
                    client={client}
                    hideBackdrop={true}
                    zIndex={10100}
                    onShowClient={() => {
                        setShowTwoWaySms(false);
                        selectKeyHandler(ClientKey, containerRef);
                    }}
                />
            )}
        </PrivatePage>
    );
};

export default Client;
